import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

import App from './App';
import { Toaster } from '@/components/ui/toaster';
import { configureAmplify } from '@/lib/amplify';
import { installGlobalErrorHandlers } from '@/lib/errorReporter';
import { useAuthStore } from '@/stores/authStore';
import { isBackstagePath } from './backstage/config';
// Fonts ship inside our own bundle rather than loading from Google, which would
// hand every visitor's IP address to Google before they had agreed to anything
// (#218). Inter and Playfair Display are variable fonts — one file covers every
// weight, which is what Google was serving too.
import '@fontsource-variable/inter';
import '@fontsource-variable/playfair-display';
import '@fontsource-variable/playfair-display/wght-italic.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import './index.css';

function bootCustomer() {
  // Configure AWS Amplify
  configureAmplify();

  // Report uncaught errors + unhandled promise rejections to CloudWatch via the
  // client-errors Function URL. Attaches userId if the user is signed in.
  installGlobalErrorHandlers(() => useAuthStore.getState().user?.userId ?? null);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 3,
        refetchOnWindowFocus: false,
      },
    },
  });

  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
            <Toaster />
          </BrowserRouter>
        </QueryClientProvider>
      </HelmetProvider>
    </StrictMode>
  );
}

// Staff pages load a separate app, only when the address is the Backstage one,
// so customers never download staff code.
if (isBackstagePath(window.location.pathname)) {
  import('./backstage/boot').then((m) => m.bootBackstage(document.getElementById('root')!));
} else {
  bootCustomer();
}
